import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreatePlatformMutation,
  UpdatePlatformMutation,
  PlatformEditQuery,
  PlatformShowQuery,
  type UpdatePlatformInput,
} from "@/admin/entities/platforms";
import { OrganizationsAutocompleteQuery } from "@/admin/entities/organizations";
import { MailerConfigsAutocompleteQuery } from "@/admin/entities/mailerConfigs";
import { LanguagesAutocompleteQuery } from "@/admin/entities/languages";
import type {
  PlatformEditQueryType,
  PlatformShowQueryType,
} from "@/types/graphql/admin.generated";
import { ArrayItemRow } from "@/shared/components";
import {
  PARALLEL_PREMIUM_VIEW_LIMIT_FIELD,
  parallelLimitEditValue,
} from "./parallelLimitField";
import {
  PLATFORM_FEATURE_OPTIONS,
  toPlatformFeaturesInput,
} from "./platformFeatures";

/**
 * Схемы форм для feature управления платформами
 * Используют entity мутации и запросы
 */

// Статусы платформ
const statusOptions = [
  { label: "Включена", value: "enabled" },
  { label: "Черновик", value: "draft" },
  { label: "Отключена", value: "disabled" },
];

// Фильтры списка платформ
export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    placeholder: "Поиск по названию...",
    xs: 12,
    fullWidth: true,
  },
  host: {
    priority: "secondary",
    label: "Хост",
    type: "text",
    xs: 3,
    fullWidth: true,
  },
  slug: {
    priority: "secondary",
    label: "Слаг",
    type: "text",
    xs: 3,
    fullWidth: true,
  },
  status: {
    priority: "quick",
    label: "Статус",
    type: "select",
    xs: 3,
    fullWidth: true,
    options: statusOptions,
  },
  organizationId: {
    priority: "secondary",
    label: "Организация",
    type: "autocomplete",
    query: OrganizationsAutocompleteQuery,
    xs: 3,
    fullWidth: true,
  },
};

// Основная схема для создания и редактирования
const mainSchema: Schema = {
  title: {
    required: true,
    label: "Название",
    type: "text",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  organizationId: {
    required: true,
    label: "Организация",
    type: "autocomplete",
    query: OrganizationsAutocompleteQuery,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  description: {
    label: "Описание",
    type: "text",
    xs: 12,
    multiline: true,
    rows: 4,
    fullWidth: true,
  },
  slug: {
    required: true,
    label: "Слаг",
    type: "text",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  host: {
    label: "Хост",
    type: "text",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  status: {
    label: "Статус",
    type: "select",
    xs: 12,
    md: 4,
    fullWidth: true,
    options: statusOptions,
  },
  mailerConfigId: {
    label: "Конфигурация почты",
    type: "autocomplete",
    query: MailerConfigsAutocompleteQuery,
    xs: 12,
    fullWidth: true,
  },
  languageId: {
    label: "Язык платформы",
    type: "autocomplete",
    query: LanguagesAutocompleteQuery,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
};

// Схема создания платформы
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Основные настройки",
    createMutation: CreatePlatformMutation,
    schema: mainSchema,
  },
} as const;

// Схема редактирования платформы
export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Детали платформы",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const { platform } = data as PlatformShowQueryType;
      if (!platform) return null;
      return platform.title;
    },
    showQuery: PlatformShowQuery,
  },
  main: {
    label: "Основные настройки",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const { platform } = data as PlatformShowQueryType;
      if (!platform) return null;
      return platform.title;
    },
    updateMutation: UpdatePlatformMutation,
    editQuery: PlatformEditQuery,
    schema: {
      ...mainSchema,
      gaId: {
        label: "Google Analytics ID",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      googleSiteVerification: {
        type: "text",
        label: "Google Site Verification",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      // Поле есть в бэкенде с фазы 1 (создание видео провайдера), но
      // интерфейса у него не было. Добавлено только в редактирование, а не
      // в общую mainSchema: CreatePlatformInput его не принимает.
      videoProvider: {
        type: "select",
        label: "Провайдер видео",
        options: [
          { value: "", label: "Дефолт инстанса" },
          { value: "CLOUDFLARE", label: "Cloudflare Stream" },
          { value: "BUNNY", label: "Bunny Stream" },
        ],
        xs: 12,
        sm: 6,
        fullWidth: true,
      },
    },
    getEditData: (data: unknown): UpdatePlatformInput => {
      // Returns UpdatePlatformInput
      const { platform } = data as PlatformEditQueryType;
      return {
        title: platform.title,
        description: platform.description,
        host: platform.host,
        slug: platform.slug,
        status: platform.status,
        organizationId: platform.organizationId || null,
        mailerConfigId: platform.mailerConfigId || null,
        languageId: platform.language?.id || null,
        gaId: platform.gaId,
        googleSiteVerification: platform.googleSiteVerification,
        videoProvider: platform.videoProvider ?? null,
      };
    },
  },
  visual: {
    label: "Внешний вид",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const { platform } = data as PlatformShowQueryType;
      if (!platform) return null;
      return platform.title;
    },
    updateMutation: UpdatePlatformMutation,
    editQuery: PlatformEditQuery,
    schema: {
      logoId: {
        label: "Логотип",
        type: "image",
        imageType: "avatar",
        helperText: "Минимальный размер: 240x240px. Форматы: jpeg, png.",
      },
      faviconId: {
        label: "Favicon",
        type: "image",
        imageType: "avatar",
        helperText: "Минимальный размер: 240x240px. Форматы: jpeg, png.",
      },
      "theme.palette.type": {
        label: "Тема",
        type: "select",
        xs: 12,
        required: true,
        fullWidth: true,
        options: [
          { label: "Темная", value: "dark" },
          { label: "Светлая", value: "light" },
        ],
      },
      "theme.palette.primary": {
        label: "Primary color",
        type: "color",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      "theme.palette.secondary": {
        label: "Secondary color",
        type: "color",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      "theme.palette.backgroundColor": {
        label: "Background color",
        type: "color",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      "theme.typography.fontFamily": {
        label: "Шрифт текста",
        helperText:
          "CSS font-family, пример: 'Manrope', sans-serif. Пусто: Manrope",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      "theme.typography.headingFontFamily": {
        label: "Шрифт заголовков",
        helperText:
          "CSS font-family для заголовков и кнопок, пример: 'Archivo', sans-serif. Пусто: Archivo",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      hideableHeader: {
        label: "Скрываемый хедер",
        helperText:
          "Хедер будет скрываться при прокрутке вниз и появляться при прокрутке вверх",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      isLikesEnabled: {
        label: "Лайки",
        helperText: "Лайки и дизлайки под видео на сайте",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      isFavoritesEnabled: {
        label: "Избранное",
        helperText: "Кнопка «Сохранить» под видео",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      isFeaturedVideosEnabled: {
        label: "Похожие видео",
        helperText:
          "Блок похожих видео на странице видео и автопереход к следующему",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      isStreamChatEnabled: {
        label: "Чат в стримах",
        helperText: "Чат под трансляциями на сайте",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      isVideoChatEnabled: {
        label: "Чат под видео",
        helperText: "Комментарии под видео на сайте",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      features: {
        label: "Возможности платформы",
        helperText:
          "Фичефлаги: живые реакции, опросы, рассылки. Партнёр видит только включённые",
        type: "select",
        multiple: true,
        xs: 12,
        fullWidth: true,
        options: PLATFORM_FEATURE_OPTIONS,
      },
      marketingMonthlyQuota: {
        label: "Квота писем рассылок в месяц",
        type: "number",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText: "Пусто: 300. Считается по UTC-месяцу",
      },
      marketingJourneyEntriesPerDay: {
        label: "Лимит входа в цепочку в сутки",
        type: "number",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText:
          "Пусто: 2000. Сверх лимита зрители в цепочку не входят, отказы видны в шапке цепочки",
      },
      parallelPremiumViewLimit: PARALLEL_PREMIUM_VIEW_LIMIT_FIELD,
      isLiveReactionsEnabled: {
        label: "Реакции на эфирах",
        helperText:
          "Тумблер партнёра. Работает, только когда включён флаг «Живые реакции на эфирах»",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      showSupportEmail: {
        label: "Показывать email поддержки",
        type: "switch",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      supportEmail: {
        label: "Email поддержки",
        type: "text",
        xs: 12,
        fullWidth: true,
      },
      showCookieBanner: {
        label: "Показывать баннер куки",
        helperText: "Показывать баннер с согласием на использование куков",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      "footer.title": {
        type: "text",
        label: "Заголовок футера",
        helperText: "Плейсхолдеры: {date}",
        xs: 12,
        fullWidth: true,
      },
      "footer.subtitle": {
        type: "text",
        label: "Подзаголовок футера",
        helperText: "Плейсхолдеры: {date}",
        xs: 12,
        fullWidth: true,
      },
      defaultRowSize: {
        label: "Размер ряда по умолчанию",
        type: "select",
        xs: 6,
        fullWidth: true,
        helperText: "Количество элементов в ряду по умолчанию",
        options: [
          { label: "2", value: 2 },
          { label: "3", value: 3 },
          { label: "4", value: 4 },
          { label: "6", value: 6 },
        ],
      },

      _termsSection: {
        type: "title",
        label: "Правила и политики",
        xs: 12,
      },
      showTerms: {
        label: "Отображать в футере",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      acceptTermsOfServiceOnSignIn: {
        label: "Требовать согласие при входе",
        helperText:
          "Показывает чекбокс согласия с условиями использования и политикой конфиденциальности на форме входа; без отметки кнопка отправки кода заблокирована",
        type: "switch",
        xs: 12,
        fullWidth: true,
      },
      privacyPolicyUrl: {
        type: "text",
        label: "Ссылка на политику конфиденциальности",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      termsOfServiceUrl: {
        type: "text",
        label: "Ссылка на условия использования",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      refundPolicyUrl: {
        type: "text",
        label: "Ссылка на политику возврата",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      customTerms: {
        type: "array",
        label: "Дополнительные ссылки",
        addText: "Добавить ссылку",
        subschema: {
          label: {
            label: "Название",
            type: "text",
            xs: 6,
            fullWidth: true,
            required: true,
          },
          url: {
            label: "Ссылка",
            type: "text",
            xs: 6,
            fullWidth: true,
            required: true,
          },
        },
        itemComponent: ArrayItemRow,
        xs: 12,
      },

      "footer.social": {
        type: "array",
        label: "Социальные сети",
        addText: "Добавить ссылку",
        xs: 12,
        subschema: {
          type: {
            type: "select",
            label: "Тип",
            xs: 4,
            fullWidth: true,
            options: [
              { value: "facebook", label: "FACEBOOK" },
              { value: "twitter", label: "TWITTER" },
              { value: "youtube", label: "YOUTUBE" },
              { value: "odnoklassniki", label: "ODNOKLASSNIKI" },
              { value: "instagram", label: "INSTAGRAM" },
              { value: "telegram", label: "TELEGRAM" },
              { value: "vkontakte", label: "VKONTAKTE" },
              { value: "tiktok", label: "TIKTOK" },
            ],
          },
          link: {
            type: "text",
            label: "Ссылка",
            xs: 8,
            fullWidth: true,
          },
        },
        itemComponent: ArrayItemRow,
      },

      customHead: {
        label: "Пользовательский HTML в <head>",
        type: "text",
        xs: 12,
        fullWidth: true,
        multiline: true,
      },
      customHTML: {
        label: "Пользовательский HTML в <body>",
        type: "text",
        xs: 12,
        fullWidth: true,
        multiline: true,
      },
    },
    getEditData: (data: unknown): UpdatePlatformInput => {
      // Returns UpdatePlatformInput
      const { platform } = data as PlatformEditQueryType;
      return {
        logoId: platform.logo?.id || null,
        faviconId: platform.favicon?.id || null,
        defaultRowSize: platform.defaultRowSize,
        description: platform.description,
        privacyPolicyUrl: platform.privacyPolicyUrl,
        termsOfServiceUrl: platform.termsOfServiceUrl,
        refundPolicyUrl: platform.refundPolicyUrl,
        customTerms: (platform.customTerms || []).map((term) => ({
          label: term.label,
          url: term.url,
        })),
        customHTML: platform.customHTML,
        customHead: platform.customHead,
        theme: platform.theme
          ? {
              palette: {
                type: platform.theme.palette?.type,
                primary: platform.theme.palette?.primary,
                secondary: platform.theme.palette?.secondary,
                backgroundColor: platform.theme.palette?.backgroundColor,
              },
              typography: {
                fontFamily: platform.theme.typography?.fontFamily,
                headingFontFamily: platform.theme.typography?.headingFontFamily,
              },
            }
          : undefined,
        hideableHeader: platform.hideableHeader,
        isLikesEnabled: platform.isLikesEnabled,
        isFavoritesEnabled: platform.isFavoritesEnabled,
        isFeaturedVideosEnabled: platform.isFeaturedVideosEnabled,
        isStreamChatEnabled: platform.isStreamChatEnabled,
        isVideoChatEnabled: platform.isVideoChatEnabled,
        features: toPlatformFeaturesInput(platform.features),
        marketingMonthlyQuota: platform.marketingMonthlyQuota ?? null,
        marketingJourneyEntriesPerDay:
          platform.marketingJourneyEntriesPerDay ?? null,
        isLiveReactionsEnabled: platform.isLiveReactionsEnabled,
        parallelPremiumViewLimit: parallelLimitEditValue(platform),
        showSupportEmail: platform.showSupportEmail,
        supportEmail: platform.supportEmail,
        footer: platform.footer
          ? {
              title: platform.footer.title,
              subtitle: platform.footer.subtitle,
              social: (platform.footer.social || []).map((social) => ({
                type: social.type,
                link: social.link,
              })),
            }
          : undefined,

        showTerms: platform.showTerms,
        showCookieBanner: platform.showCookieBanner,
        acceptTermsOfServiceOnSignIn: platform.acceptTermsOfServiceOnSignIn,
      };
    },
  },
  clientFields: {
    label: "Клиенты",
    type: "form",
    paper: false,
    getTitle: (data: unknown) => {
      const { platform } = data as PlatformShowQueryType;
      if (!platform) return null;
      return platform.title;
    },
    updateMutation: UpdatePlatformMutation,
    editQuery: PlatformEditQuery,
    schema: {
      clientFields: {
        label: "Дополнительные поля",
        type: "customFieldsSchema",
        xs: 12,
      },
    },
    getEditData: (data: unknown): UpdatePlatformInput => {
      // Returns UpdatePlatformInput
      const { platform } = data as PlatformEditQueryType;
      return {
        clientFields: (platform.clientFields || []).map((i) => ({
          type: i.type,
          label: i.label,
          name: i.name,
          required: i.required ?? false,
          multiple: i.multiple ?? false,
          placeholder: i.placeholder ?? "",
          helperText: i.helperText ?? "",
          options: i.options?.map((o) => ({
            value: o?.value || "",
            label: o?.label || "",
          })),
        })),
      };
    },
  },
  assistant: {
    label: "Помощник",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const { platform } = data as PlatformShowQueryType;
      if (!platform) return null;
      return platform.title;
    },
    updateMutation: UpdatePlatformMutation,
    editQuery: PlatformEditQuery,
    schema: {
      "assistant.enabled": {
        label: "Помощник включён",
        type: "checkbox",
      },
      "assistant.name": {
        label: "Имя",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText: "Пусто: Kit",
      },
      "assistant.avatarId": {
        label: "Аватар",
        type: "image",
        imageType: "avatar",
        helperText: "Пусто: аватар по умолчанию",
      },
      "assistant.notes": {
        label: "Заметки для помощника",
        type: "text",
        multiline: true,
        rows: 6,
        xs: 12,
        fullWidth: true,
        maxLength: 4000,
        nullable: true,
        helperText:
          "Особенности арены: сроки возврата, когда появляются записи эфиров. До 4000 символов, важнее общей базы знаний.",
      },
      "assistant.model": {
        label: "Модель",
        type: "select",
        xs: 12,
        md: 6,
        fullWidth: true,
        options: [
          {
            value: "openai/gpt-6-luna",
            label: "GPT-6 Luna, OpenRouter (по умолчанию)",
          },
          { value: "claude-sonnet-5", label: "Claude Sonnet 5" },
          { value: "claude-opus-5", label: "Claude Opus 5" },
          { value: "claude-haiku-4-5", label: "Claude Haiku 4.5" },
        ],
      },
      "assistant.monthlyBudgetUsd": {
        label: "Бюджет в месяц, $",
        type: "number",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText: "Пусто: 50",
      },
      "assistant.dailyMessagesPerViewer": {
        label: "Сообщений зрителя в сутки",
        type: "number",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText: "Пусто: 30",
      },
    },
    getEditData: (data: unknown): UpdatePlatformInput => {
      // Returns UpdatePlatformInput
      const { platform } = data as PlatformEditQueryType;
      const assistant = platform.assistant;
      return {
        assistant: {
          enabled: assistant?.enabled ?? false,
          name: assistant?.name ?? "",
          avatarId: assistant?.avatar?.id ?? null,
          notes: assistant?.notes ?? "",
          model: assistant?.model ?? "openai/gpt-6-luna",
          monthlyBudgetUsd: assistant?.monthlyBudgetUsd ?? null,
          dailyMessagesPerViewer: assistant?.dailyMessagesPerViewer ?? null,
        },
      };
    },
  },
} as const;

export function createEditSchema(
  showComponent: React.ComponentType<{ editData?: unknown; id?: string }>
): EditSchemaDefinition {
  return {
    ...editSchemaBase,
    details: {
      ...editSchemaBase.details,
      showComponent: showComponent,
    },
  };
}
